export type TMonitoringArticleSentiment = 'negative' | 'neutral' | 'positive';

export type TMonitoringRiskSeverity = 'low' | 'medium' | 'high' | 'critical';

export type TMonitoringImpactLevel = 'low' | 'medium' | 'high' | 'critical';

export type TMonitoringSourceType = 'official' | 'industry' | 'media' | 'legal-analysis';

export type TMonitoringApprovalState =
  | 'rumor'
  | 'under-review'
  | 'approved-not-effective'
  | 'in-force'
  | 'enforcement-action';

export interface IMonitoringArticle {
  title: string,
  date: string,
  source: string,
  url?: string,
  summary?: string,
  keywords_found?: string[],
  sentiment: TMonitoringArticleSentiment,
  risk_severity: TMonitoringRiskSeverity,
  confidence?: number,
  excerpt?: string,
}

/*
 * Media Sentiment (`reputation-monitoring` / `media-sentiment`) persisted shapes.
 * Source of truth: the Lambda that writes them (attaima-ai-agents
 * `SupplyChainAgents/media-sentiment/media_sentiment/media_runner.py`).
 * Mirrored in node-attaima `TVCAgentOutput` (`value-chain/agents/types/ai-task-payloads.types.ts`)
 * under the same names. Update both when the Lambda changes.
 */

/** `agent_runs.output.summary` of a media sentiment run. */
export interface IMonitoringResultsSummary {
  /**
   * Count from the LLM summary when it is an integer, else the count of articles.
   * It can be different from the count of finding rows (the Lambda keeps max `max_articles` articles).
   */
  total_articles: number,
  /** Count of articles with `risk_severity` `high` or `critical`. */
  high_risk_count: number,
  /** Keyword → count of articles that contain it. */
  keywords_frequency: Record<string, number>,
}

/**
 * `payloadJson` of an `agent_run_findings` row with `findingType = media_article`.
 * The Lambda normalizes each LLM article, so every key is always present.
 * A text field is `''` when the LLM gives no value.
 * The typed columns hold copies: `title` (first 500 characters), `severity` ← `risk_severity`,
 * `confidenceScore` ← `confidence`, `sourceUrl` ← `url` (`null` when empty),
 * `observedAt` ← parsed `date` (`null` when it cannot be parsed),
 * `description` ← `summary` + `Keywords: …` line (`null` when both are empty).
 */
export type TMediaArticleFindingPayload = {
  /** `'Untitled'` when the LLM gives no title. Not truncated. */
  title: string,
  /** Date text from the LLM. The prompt asks for `YYYY-MM-DD`. */
  date: string,
  source: string,
  url: string,
  summary: string,
  keywords_found: string[],
  /** Lowercase LLM value. The Lambda does not validate it. `'neutral'` when missing. */
  sentiment: TMonitoringArticleSentiment | string,
  /** Normalized. The Lambda changes an unknown value to `'low'`. */
  risk_severity: TMonitoringRiskSeverity,
  /** 0–1. `0.5` when missing or not a number. */
  confidence: number,
  excerpt: string,
};

/** `metadataJson` of a `media_article` finding row. The values are copies of payload fields. */
export type TMediaArticleFindingMetadata = Pick<
TMediaArticleFindingPayload,
'source' | 'date' | 'sentiment' | 'excerpt'
>;

/** Reputation / media-sentiment results (ATT-57) */
export interface IReputationMonitoringSignalResult {
  articles_found: IMonitoringArticle[],
  summary: IMonitoringResultsSummary,
}

export interface IRegulationFactoryLocation {
  label: string,
  country?: string,
  region?: string,
}

export interface IRegulationSourceLink {
  label: string,
  url: string,
}

/*
 * Regulation Tracking output. Source of truth for this shape (hub repo).
 * Mirrored in node-attaima `TVCAgentOutput` (`value-chain/agents/types/ai-task-payloads.types.ts`)
 * under the same names. Update both when a field changes.
 */
export interface IRegulationSignal {
  id: string,
  /** Short headline. The Lambda copies it (or `regulation_name`) to the finding `title` column. */
  title?: string,
  product_name: string,
  product_unit_id: number,
  related_materials?: string[],
  factory_location?: IRegulationFactoryLocation,
  regulation_name: string,
  regulatory_body: string,
  jurisdiction: string,
  source_type: TMonitoringSourceType,
  source_links: IRegulationSourceLink[],
  impact_level: TMonitoringImpactLevel,
  impact_summary?: string,
  approval_state: TMonitoringApprovalState,
  enacted_date?: string | null,
  effective_date?: string | null,
  expected_enforcement_date?: string | null,
  confidence?: number,
  recommended_action?: string,
}

export interface IRegulationResultsSummary {
  total_results: number,
  high_impact_count: number,
  critical_impact_count: number,
}

/**
 * `payloadJson` of an `agent_run_findings` row with `findingType = regulation_signal`.
 * The Lambda stores the raw LLM item as is, so every key is optional.
 * Prefer the typed finding columns (`severity`, `title`, `description`, `sourceUrl`, …).
 */
export type TRegulationFindingPayload = Partial<IRegulationSignal>;

/** Regulation-tracking results (ATT-58) */
export interface IRegulationMonitoringSignalResult {
  regulations_found: IRegulationSignal[],
  summary: IRegulationResultsSummary,
}

export type TFinanceFindingTheme =
  | 'liquidity'
  | 'credit'
  | 'solvency'
  | 'insolvency'
  | 'payment-behaviour'
  | 'market'
  | 'capacity'
  | 'earnings';

export type TFinanceBankruptcyStatusValue =
  | 'none'
  | 'watch'
  | 'chapter-11'
  | 'bankruptcy-protection'
  | 'judicial-reorganization'
  | 'liquidation'
  | 'insolvency-proceedings'
  | 'emerged';

export interface IFinanceFindingMetric {
  name: string,
  formula?: string,
  value: number | null,
  unit?: string,
  prior_value?: number | null,
  trend?: 'declining' | 'stable' | 'improving',
  year?: number,
  components?: {
    liquid_assets?: number,
    current_liabilities?: number,
  },
}

/** Mock UI shape (ATT-59). A stored finance finding is `TFinanceFindingPayload`. */
export interface IFinanceFinding {
  id: string,
  supplier_name: string,
  supplier_unit_id: number,
  related_materials?: string[],
  theme: TFinanceFindingTheme | string,
  title: string,
  severity: TMonitoringRiskSeverity,
  confidence?: number,
  observed_at: string,
  source_type?: string,
  source_label?: string,
  source_url?: string,
  metric?: IFinanceFindingMetric,
  shortage_risk?: {
    level: string,
    summary: string,
  },
  recommended_action?: string,
}

export interface IFinanceProfileYear {
  year_index: number,
  fiscal_year?: number,
  revenue: number | null,
  ebitda: number | null,
  /** Option 3: liquid_assets / current_liabilities */
  liquidity_retention: number | null,
}

export interface IFinanceBankruptcyStatus {
  status: TFinanceBankruptcyStatusValue | string,
  label?: string,
  jurisdiction?: string | null,
  case_name?: string | null,
  filed_at?: string | null,
  is_active: boolean,
  history?: Array<{
    status: string,
    filed_at?: string | null,
    exited_at?: string | null,
    note?: string,
  }>,
}

export interface IFinanceEntityAlert {
  theme: string,
  title: string,
  severity: TMonitoringRiskSeverity,
  source?: string,
}

/**
 * Mock UI shape (ATT-59). The Lambda writes `TMonitoringRunEntity` items to `output.entities` instead:
 * the yearly figures and the bankruptcy status are free text in `notes`, not these fields.
 */
export interface IFinanceMonitoredEntity {
  unitId: number,
  name: string,
  stability_score: number,
  trend?: 'declining' | 'stable' | 'improving' | string,
  spike?: boolean,
  currency?: string,
  related_materials?: string[],
  financial_profile?: {
    years: IFinanceProfileYear[],
  },
  bankruptcy_status?: IFinanceBankruptcyStatus,
  alerts?: IFinanceEntityAlert[],
}

/**
 * `agent_runs.output.summary` of a finance monitoring run. Each count is the LLM's when it is
 * truthy, else the Lambda's. The template prompt asks to count an active chapter-11,
 * bankruptcy-protection or judicial-reorganization status in `high_risk_count`.
 * Mirrored in node-attaima `TVCAgentOutput` without the mock-only fields.
 */
export interface IFinanceResultsSummary {
  /** Else the count of finding rows. */
  total_findings: number,
  /** Else the count of finding rows with `severity` `high` or `critical`. */
  high_risk_count: number,
  /** Else the count of `entities`. It can differ from the count of `entities`. */
  entities_monitored: number,
  /** Mock only: the Lambda does not write it. */
  entities_under_protection_count?: number,
  /** Mock only: the Lambda does not write it. */
  themes_frequency?: Record<string, number>,
}

/** Financial-risk results (ATT-59). Mock UI shape: the Lambda saves the finance shapes below instead. */
export interface IFinanceMonitoringSignalResult {
  findings: IFinanceFinding[],
  entities: IFinanceMonitoredEntity[],
  summary: IFinanceResultsSummary,
}

export type TOwnershipFindingTheme =
  | 'reputation-contagion'
  | 'labour-rights'
  | 'environmental'
  | 'governance'
  | 'sanctions'
  | 'operational'
  | 'ownership-change';

export interface IOwnershipLinkNode {
  entity_name: string,
  role: string,
  unit_id?: number,
}

/** Mock UI shape (ATT-61). A stored ownership finding is `TOwnershipFindingPayload`. */
export interface IOwnershipFinding {
  id: string,
  supplier_name: string,
  supplier_unit_id: number,
  related_materials?: string[],
  theme: TOwnershipFindingTheme | string,
  title: string,
  severity: TMonitoringRiskSeverity,
  confidence?: number,
  observed_at: string,
  source_type?: string,
  source_label?: string,
  source_url?: string,
  link_path?: IOwnershipLinkNode[],
  contagion_mechanism?: string,
  shortage_risk?: {
    level: string,
    summary: string,
  },
  recommended_action?: string,
}

export interface IOwnershipParty {
  name: string,
  type?: 'person-or-trust' | 'company' | string,
  jurisdiction?: string,
  ownership_stake_pct?: number,
}

export interface IOwnershipInvestigationNode {
  id: string,
  name: string,
  role: string,
  jurisdiction?: string,
  ownership_stake_pct?: number,
  risk_flag?: boolean,
  finding_ids?: string[],
  children?: IOwnershipInvestigationNode[],
}

export interface IOwnershipEntityAlert {
  theme: string,
  title: string,
  severity: TMonitoringRiskSeverity,
  source?: string,
}

/** Mock UI shape (ATT-61). The Lambda writes `TMonitoringRunEntity` items to `output.entities` instead. */
export interface IOwnershipMonitoredEntity {
  unitId: number,
  name: string,
  contagion_score: number,
  trend?: 'declining' | 'stable' | 'rising' | string,
  spike?: boolean,
  related_materials?: string[],
  ownership_profile: {
    ultimate_beneficial_owner: IOwnershipParty,
    direct_parent: IOwnershipParty,
    corporate_group?: string,
    related_entities_scanned?: number,
    related_entities_flagged?: number,
    group_entities_preview?: Array<{
      name: string,
      role: string,
      jurisdiction?: string,
      risk_flag?: boolean,
    }>,
  },
  investigation_tree?: IOwnershipInvestigationNode[],
  alerts?: IOwnershipEntityAlert[],
}

/**
 * `agent_runs.output.summary` of an ownership monitoring run. Each count is the LLM's when it is
 * truthy, else the Lambda's. Mirrored in node-attaima `TVCAgentOutput` without the mock-only fields.
 */
export interface IOwnershipResultsSummary {
  /** Else the count of finding rows. */
  total_findings: number,
  /** Else the count of finding rows with `severity` `high` or `critical`. */
  high_risk_count: number,
  /** Else the count of `entities`. It can differ from the count of `entities`. */
  entities_monitored: number,
  /** Mock only: the Lambda does not write it. */
  entities_with_active_contagion_count?: number,
  /** Mock only: the Lambda does not write it. */
  themes_frequency?: Record<string, number>,
}

/** Ownership contagion results (ATT-61). Mock UI shape: the Lambda saves the shapes below instead. */
export interface IOwnershipMonitoringSignalResult {
  findings: IOwnershipFinding[],
  entities: IOwnershipMonitoredEntity[],
  summary: IOwnershipResultsSummary,
}

/*
 * Ownership Monitoring (`ownership-monitoring` / `ownership-monitoring`) persisted shapes.
 * Source of truth: the Lambda that writes them (attaima-ai-agents
 * `SupplyChainAgents/ownership-monitoring/ownership_monitoring/ownership_monitoring_runner.py`).
 * Mirrored in node-attaima `TVCAgentOutput` (`value-chain/agents/types/ai-task-payloads.types.ts`)
 * under the same names. Update both when the Lambda changes.
 */

/**
 * One source of a finding, as the LLM gives it. Not validated. The ownership, finance and climate
 * Lambdas write the same `source_links` (one runner code with other names).
 */
export type TMonitoringSourceLink = {
  label?: string,
  url?: string,
};

/**
 * `payloadJson` of an `agent_run_findings` row with `findingType = ownership_finding`.
 * The Lambda stores the raw LLM item (no validation, no normalization), so every key is optional.
 * The Lambda v1 schema asks for `title`, `severity`, `description`, `confidence` and `source_links` only.
 * The typed columns hold normalized copies: `title` ← `title` (first 500 characters, `'Untitled'`
 * when missing), `severity` ← `severity` (an unknown value becomes `'low'`), `description` ←
 * `description` or `summary`, `confidenceScore` ← `confidence` (NULL when missing), `sourceUrl` ←
 * `source_url`, `url` or `source_links[0].url`, `sourcesJson` ← `source_links` (when it is a list).
 * `observedAt` and `metadataJson` are always NULL. Read those columns first.
 */
export type TOwnershipFindingPayload = {
  title?: string,
  /** As the LLM gives it. The `severity` column holds the normalized value. */
  severity?: TMonitoringRiskSeverity | string,
  description?: string,
  /** Used for the `description` column when `description` is missing. */
  summary?: string,
  /** 0–1, as the LLM gives it. */
  confidence?: number,
  source_links?: TMonitoringSourceLink[],
  /** Used for the `sourceUrl` column before `url` and `source_links`. */
  source_url?: string,
  url?: string,
  /*
   * Not asked for by the v1 schema: present only when the LLM adds them. The mock UI shape
   * (`IOwnershipFinding`) has more such keys (`link_path`, `shortage_risk`, `supplier_name`, …).
   */
  theme?: string,
  contagion_mechanism?: string,
  recommended_action?: string,
};

/**
 * One item of `agent_runs.output.entities`: an entity the run reported (the raw LLM item, an
 * object). The ownership, finance and climate Lambdas write the same shape; the template prompt
 * says what goes in it:
 * - ownership: the corporate group, `entity_type` such as `monitored-supplier`, `direct-parent`,
 *   `ultimate-beneficial-owner` or `sister-subsidiary` (free text);
 * - finance: the supplier's financial profile, with the 1–5 year series (revenue, EBITDA,
 *   liquidity retention) and the bankruptcy status as free text in `notes`;
 * - climate: the monitored site, `entity_type` from the run input's `entity_kind`
 *   (`TClimateEntityKind`, as the LLM copies it), and its exposure summary in `notes`.
 * Not `IOwnershipMonitoredEntity`, `IFinanceMonitoredEntity` or `IClimateMonitoredEntity`, which
 * are mock UI shapes.
 */
export type TMonitoringRunEntity = {
  name?: string,
  entity_type?: string,
  /**
   * Free text: jurisdiction, stake and control (ownership); the yearly figures and bankruptcy
   * status (finance); the hazard exposure of the site (climate).
   */
  notes?: string,
};

/**
 * `agent_runs.output` of a completed ownership monitoring run. Findings are rows, not part of the
 * output. A failed run stores `{ message }` only.
 */
export type TOwnershipMonitoringOutput = {
  message: string,
  summary: IOwnershipResultsSummary,
  entities: TMonitoringRunEntity[],
};

/*
 * Financial Risk (`finance-monitoring` / `financial-risk`) persisted shapes.
 * Source of truth: the Lambda that writes them (attaima-ai-agents
 * `SupplyChainAgents/financial-risk/financial_risk/financial_risk_runner.py`). Its code is the
 * ownership runner's with other names, so the shapes match the ownership ones;
 * `TMonitoringSourceLink` and `TMonitoringRunEntity` are shared.
 * Mirrored in node-attaima `TVCAgentOutput` (`value-chain/agents/types/ai-task-payloads.types.ts`)
 * under the same names. Update both when the Lambda changes.
 */

/**
 * `payloadJson` of an `agent_run_findings` row with `findingType = financial_finding`.
 * The Lambda stores the raw LLM item (no validation, no normalization), so every key is optional.
 * The Lambda v1 schema asks for `title`, `severity`, `description`, `confidence` and `source_links` only.
 * The typed columns hold normalized copies as for `TOwnershipFindingPayload`: `title` (first 500
 * characters, `'Untitled'` when missing), `severity` (an unknown value becomes `'low'`),
 * `description` ← `description` or `summary`, `confidenceScore` ← `confidence` (NULL when missing),
 * `sourceUrl` ← `source_url`, `url` or `source_links[0].url`, `sourcesJson` ← `source_links` (when
 * it is a list). `observedAt` and `metadataJson` are always NULL. Read those columns first.
 */
export type TFinanceFindingPayload = {
  title?: string,
  /** As the LLM gives it. The `severity` column holds the normalized value. */
  severity?: TMonitoringRiskSeverity | string,
  description?: string,
  /** Used for the `description` column when `description` is missing. */
  summary?: string,
  /** 0–1, as the LLM gives it. */
  confidence?: number,
  source_links?: TMonitoringSourceLink[],
  /** Used for the `sourceUrl` column before `url` and `source_links`. */
  source_url?: string,
  url?: string,
  /*
   * Not asked for by the v1 schema: present only when the LLM adds them. The mock UI shape
   * (`IFinanceFinding`) has more such keys (`metric`, `shortage_risk`, `supplier_name`, …).
   */
  theme?: string,
  recommended_action?: string,
};

/**
 * `agent_runs.output` of a completed finance monitoring run. Findings are rows, not part of the
 * output. A failed run stores `{ message }` only.
 */
export type TFinanceMonitoringOutput = {
  message: string,
  summary: IFinanceResultsSummary,
  entities: TMonitoringRunEntity[],
};

export type TClimateEntityKind = 'supplier' | 'own-operation' | 'balance-sheet-asset';

export type TClimateFindingTheme =
  | 'flood'
  | 'storm'
  | 'heat'
  | 'wildfire'
  | 'earthquake'
  | 'drought'
  | 'crop-disease'
  | 'animal-disease'
  | 'trade-sanctions'
  | 'other-climate';

export interface IClimateLocation {
  country: string,
  region?: string | null,
  locality?: string | null,
}

export interface IClimateImpactTrailNode {
  role: string,
  entity_name: string,
}

/** Mock UI shape (ATT-60). A stored climate finding is `TClimateFindingPayload`. */
export interface IClimateFinding {
  id: string,
  entity_kind: TClimateEntityKind | string,
  entity_name: string,
  entity_unit_id: number,
  related_materials?: string[],
  location?: IClimateLocation,
  theme: TClimateFindingTheme | string,
  title: string,
  severity: TMonitoringRiskSeverity,
  confidence?: number,
  observed_at: string,
  horizon?: string,
  source_type?: string,
  source_label?: string,
  source_url?: string,
  shortage_risk?: {
    level: string,
    summary: string,
  },
  recommended_action?: string,
  impact_trail?: IClimateImpactTrailNode[],
}

export interface IClimateEntityAlert {
  theme: string,
  title: string,
  severity: TMonitoringRiskSeverity,
  source?: string,
}

/**
 * Mock UI shape (ATT-60). The Lambda writes `TMonitoringRunEntity` items to `output.entities`
 * instead: the exposure is free text in `notes`, not these fields.
 */
export interface IClimateMonitoredEntity {
  unitId: number,
  entity_kind: TClimateEntityKind | string,
  name: string,
  exposure_score: number,
  trend?: 'declining' | 'stable' | 'rising' | string,
  spike?: boolean,
  location?: IClimateLocation,
  related_materials?: string[],
  climate_profile: {
    hazards_active: string[],
    dominant_horizon?: string,
    water_stress_index?: number | null,
    notes?: string,
  },
  finding_ids?: string[],
  alerts?: IClimateEntityAlert[],
}

/**
 * `agent_runs.output.summary` of a climate monitoring run, shared with the mock UI. Each count is
 * the LLM's when it is truthy, else the Lambda's.
 */
export interface IClimateResultsSummary {
  /** Else the count of finding rows. */
  total_findings: number,
  /** Else the count of finding rows with `severity` `high` or `critical`. */
  high_risk_count: number,
  /** Else the count of `entities`. It can differ from the count of `entities`. */
  entities_monitored: number,
  /** Mock only: the Lambda does not write it. */
  entities_with_active_hazard_count?: number,
  /** Mock only: the Lambda does not write it. */
  themes_frequency?: Record<string, number>,
}

/** Climate / geo-hazard results (ATT-60). Mock UI shape: the Lambda saves the shapes below instead. */
export interface IClimateMonitoringSignalResult {
  findings: IClimateFinding[],
  entities: IClimateMonitoredEntity[],
  summary: IClimateResultsSummary,
}

/*
 * Climate Risk (`climate-monitoring` / `climate-risk`) persisted shapes.
 * Source of truth: the Lambda that writes them (attaima-ai-agents
 * `SupplyChainAgents/climate-risk/climate_risk/climate_risk_runner.py`). Its code is the
 * ownership and finance runners' with other names, so the shapes match theirs;
 * `TMonitoringSourceLink` and `TMonitoringRunEntity` are shared.
 * Mirrored in node-attaima `TVCAgentOutput` (`value-chain/agents/types/ai-task-payloads.types.ts`)
 * under the same names. Update both when the Lambda changes.
 */

/**
 * `payloadJson` of an `agent_run_findings` row with `findingType = climate_finding`.
 * The Lambda stores the raw LLM item (no validation, no normalization), so every key is optional.
 * The Lambda v1 schema asks for `title`, `severity`, `description`, `confidence` and `source_links` only.
 * The template prompt asks the LLM to name the hazard, the location, the likely impact and the
 * horizon in `description`, not as keys.
 * The typed columns hold normalized copies as for `TOwnershipFindingPayload`: `title` (first 500
 * characters, `'Untitled'` when missing), `severity` (an unknown value becomes `'low'`),
 * `description` ← `description` or `summary`, `confidenceScore` ← `confidence` (NULL when missing),
 * `sourceUrl` ← `source_url`, `url` or `source_links[0].url`, `sourcesJson` ← `source_links` (when
 * it is a list). `observedAt` and `metadataJson` are always NULL. Read those columns first.
 */
export type TClimateFindingPayload = {
  title?: string,
  /** As the LLM gives it. The `severity` column holds the normalized value. */
  severity?: TMonitoringRiskSeverity | string,
  description?: string,
  /** Used for the `description` column when `description` is missing. */
  summary?: string,
  /** 0–1, as the LLM gives it. */
  confidence?: number,
  source_links?: TMonitoringSourceLink[],
  /** Used for the `sourceUrl` column before `url` and `source_links`. */
  source_url?: string,
  url?: string,
  /*
   * Not asked for by the v1 schema: present only when the LLM adds them. The mock UI shape
   * (`IClimateFinding`) has more such keys (`location`, `related_materials`, `shortage_risk`,
   * `impact_trail`, …).
   */
  theme?: string,
  horizon?: string,
  recommended_action?: string,
};

/**
 * `agent_runs.output` of a completed climate monitoring run. Findings are rows, not part of the
 * output. A failed run stores `{ message }` only.
 */
export type TClimateMonitoringOutput = {
  message: string,
  summary: IClimateResultsSummary,
  entities: TMonitoringRunEntity[],
};

/** @deprecated Prefer IReputationMonitoringSignalResult — kept for reputation call sites */
export type IMonitoringSignalResult = IReputationMonitoringSignalResult;

export type TMonitoringAnySignalResult =
  | IReputationMonitoringSignalResult
  | IRegulationMonitoringSignalResult
  | IFinanceMonitoringSignalResult
  | IOwnershipMonitoringSignalResult
  | IClimateMonitoringSignalResult;

const hasOwnershipEntityShape = (
  result: TMonitoringAnySignalResult,
): boolean => (
  'entities' in result
  && Array.isArray(result.entities)
  && result.entities.some((entity) => (
    entity
    && typeof entity === 'object'
    && ('contagion_score' in entity || 'ownership_profile' in entity)
  ))
);

const hasClimateEntityShape = (
  result: TMonitoringAnySignalResult,
): boolean => (
  'entities' in result
  && Array.isArray(result.entities)
  && result.entities.some((entity) => (
    entity
    && typeof entity === 'object'
    && ('exposure_score' in entity || 'climate_profile' in entity)
  ))
);

export const isReputationMonitoringSignalResult = (
  result: TMonitoringAnySignalResult | null | undefined,
): result is IReputationMonitoringSignalResult => (
  Boolean(result && 'articles_found' in result)
);

export const isRegulationMonitoringSignalResult = (
  result: TMonitoringAnySignalResult | null | undefined,
): result is IRegulationMonitoringSignalResult => (
  Boolean(result && 'regulations_found' in result)
);

export const isOwnershipMonitoringSignalResult = (
  result: TMonitoringAnySignalResult | null | undefined,
): result is IOwnershipMonitoringSignalResult => (
  Boolean(
    result
    && ('findings' in result || 'entities' in result)
    && !('articles_found' in result)
    && !('regulations_found' in result)
    && hasOwnershipEntityShape(result),
  )
);

export const isClimateMonitoringSignalResult = (
  result: TMonitoringAnySignalResult | null | undefined,
): result is IClimateMonitoringSignalResult => (
  Boolean(
    result
    && ('findings' in result || 'entities' in result)
    && !('articles_found' in result)
    && !('regulations_found' in result)
    && hasClimateEntityShape(result),
  )
);

export const isFinanceMonitoringSignalResult = (
  result: TMonitoringAnySignalResult | null | undefined,
): result is IFinanceMonitoringSignalResult => (
  Boolean(
    result
    && ('findings' in result || 'entities' in result)
    && !('articles_found' in result)
    && !('regulations_found' in result)
    && !hasOwnershipEntityShape(result)
    && !hasClimateEntityShape(result),
  )
);

export default IMonitoringSignalResult;
